import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Camera, CameraOff, ShieldCheck, ShieldAlert, ScanLine,
  Loader2, RotateCcw, Type, Store
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const READER_ID = 'verify-badge-reader';

const TIER_LABEL = {
  platinum: 'Platinum',
  gold: 'Gold',
  trusted: 'Trusted',
  new: 'New',
};

/**
 * /verify-badge — Door-staff PWA scanner for Trusted Guest badges.
 * Mobile-first. No login. Camera scans QR → calls /api/trusted-guest/public/{handle}
 * and shows a full-screen pass/fail card with age + tier in under a second.
 */
export default function VerifyBadgePage() {
  const [view, setView] = useState('idle'); // idle | scanning | checking | result | manual
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [requiredAge, setRequiredAge] = useState(18);
  const [manualHandle, setManualHandle] = useState('');
  const [venue, setVenue] = useState(null); // {partner_code, name, type}
  const [searchParams] = useSearchParams();
  const scannerRef = useRef(null);
  const stoppingRef = useRef(false);

  // Resolve venue attribution: ?venue=CODE takes precedence, else localStorage
  useEffect(() => {
    const codeFromUrl = (searchParams.get('venue') || '').toUpperCase();
    const storedCode = (localStorage.getItem('bhermes_venue_code') || '').toUpperCase();
    const code = codeFromUrl || storedCode;
    if (!code) return;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/venue-partners/by-code/${code}`);
        setVenue(r.data);
        localStorage.setItem('bhermes_venue_code', r.data.partner_code);
        localStorage.setItem('bhermes_venue_name', r.data.name);
      } catch {
        // invalid code: drop it silently
        if (codeFromUrl) localStorage.removeItem('bhermes_venue_code');
      }
    })();
  }, [searchParams]);

  const stopScanner = useCallback(async () => {
    if (!scannerRef.current || stoppingRef.current) return;
    stoppingRef.current = true;
    try {
      const state = scannerRef.current.getState();
      // 2 = SCANNING, 3 = PAUSED
      if (state === 2 || state === 3) await scannerRef.current.stop();
      await scannerRef.current.clear();
    } catch { /* swallow */ }
    scannerRef.current = null;
    stoppingRef.current = false;
  }, []);

  const verify = useCallback(async (handle) => {
    setView('checking');
    setError(null);
    let outcome = 'error';
    try {
      const r = await axios.get(`${BACKEND_URL}/api/trusted-guest/public/${handle}`);
      setResult(r.data);
      setView('result');
      outcome = (r.data.age_verified && (r.data.age_bucket || 0) >= requiredAge) ? 'pass' : 'deny';
    } catch (e) {
      setError(e?.response?.status === 404 ? 'Badge not found or has been disabled.' : 'Could not verify. Try again.');
      setView('result');
      outcome = 'error';
    }
    // Fire-and-forget attribution scan (only when venue is known + handle looks valid)
    if (venue?.partner_code && /^tg_[a-f0-9]{12}$/i.test(handle)) {
      axios.post(`${BACKEND_URL}/api/venue-partners/scan`, {
        handle,
        partner_code: venue.partner_code,
        age_mode: requiredAge,
        result: outcome,
      }).catch(() => { /* non-critical */ });
    }
  }, [requiredAge, venue]);

  const onScanSuccess = useCallback(async (text) => {
    // Extract handle from full URL or raw handle
    const m = text.match(/tg_[a-f0-9]{12}/i);
    const handle = m ? m[0] : text.trim();
    await stopScanner();
    verify(handle);
  }, [stopScanner, verify]);

  const startScanner = useCallback(async () => {
    setError(null);
    setResult(null);
    setView('scanning');
    // Wait one tick so the reader div is in the DOM
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode(READER_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          onScanSuccess,
          () => { /* per-frame errors are noise */ }
        );
      } catch (e) {
        setError('Camera unavailable. ' + (e?.message || 'Permission denied?'));
        setView('idle');
      }
    }, 50);
  }, [onScanSuccess]);

  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const reset = async () => {
    await stopScanner();
    setResult(null);
    setError(null);
    setManualHandle('');
    setView('idle');
  };

  const submitManual = (e) => {
    e.preventDefault();
    const h = manualHandle.trim();
    if (!h) return;
    const m = h.match(/tg_[a-f0-9]{12}/i);
    verify(m ? m[0] : h);
  };

  // Result interpretation
  const passed = result && result.age_verified && (result.age_bucket || 0) >= requiredAge;

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100 flex flex-col" data-testid="verify-badge-page">
      <header className="border-b border-zinc-800/60 px-5 py-4 flex justify-between items-center">
        <Link to="/" className="text-xs tracking-widest text-zinc-400 hover:text-zinc-200">
          BHERMES · Door Verifier
        </Link>
        <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] tracking-widest">
          {requiredAge}+ MODE
        </Badge>
      </header>

      {venue ? (
        <div className="px-5 py-2 bg-amber-500/10 border-b border-amber-500/20 text-center" data-testid="venue-banner">
          <p className="text-xs text-amber-300">
            Scanning as <b>{venue.name}</b> · <span className="font-mono text-amber-400">{venue.partner_code}</span>
          </p>
        </div>
      ) : (
        <div className="px-5 py-2 bg-zinc-900/50 border-b border-zinc-800/50 text-center">
          <Link to="/verify-badge/venue" className="text-xs text-zinc-400 hover:text-amber-400 inline-flex items-center gap-1" data-testid="become-partner-link">
            <Store className="w-3 h-3" /> Become a verification partner — your scans count toward guest trust
          </Link>
        </div>
      )}

      <main className="flex-1 px-5 py-6 max-w-md w-full mx-auto">
        {view === 'idle' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 space-y-5">
              <div className="text-center space-y-1">
                <ScanLine className="w-12 h-12 text-amber-400 mx-auto" />
                <h1 className="text-2xl font-semibold">Scan a Trusted Badge</h1>
                <p className="text-sm text-zinc-400">
                  Point at the guest's QR code. Result appears in under a second.
                </p>
              </div>

              <div>
                <p className="text-xs tracking-widest text-zinc-500 mb-2">REQUIRED AGE</p>
                <div className="grid grid-cols-3 gap-2">
                  {[18, 21, 25].map((n) => (
                    <Button
                      key={n}
                      variant={requiredAge === n ? 'default' : 'outline'}
                      className={requiredAge === n
                        ? 'bg-amber-500 hover:bg-amber-600 text-black'
                        : 'border-zinc-700 text-zinc-200'}
                      onClick={() => setRequiredAge(n)}
                      data-testid={`age-mode-${n}`}>
                      {n}+
                    </Button>
                  ))}
                </div>
              </div>

              <Button
                size="lg"
                className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                onClick={startScanner}
                data-testid="start-scanner-btn">
                <Camera className="w-5 h-5 mr-2" /> Start camera
              </Button>

              <Button
                variant="ghost"
                className="w-full text-zinc-400"
                onClick={() => setView('manual')}
                data-testid="manual-entry-btn">
                <Type className="w-4 h-4 mr-2" /> Type handle manually
              </Button>
            </CardContent>
          </Card>
        )}

        {view === 'scanning' && (
          <div className="space-y-4">
            <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
              <div id={READER_ID} className="w-full aspect-square bg-black" data-testid="scanner-viewport" />
            </Card>
            <p className="text-center text-sm text-zinc-400">Hold the QR steady inside the frame</p>
            <Button
              variant="outline"
              className="w-full border-zinc-700 text-zinc-200"
              onClick={reset}
              data-testid="cancel-scan-btn">
              <CameraOff className="w-4 h-4 mr-2" /> Cancel
            </Button>
          </div>
        )}

        {view === 'manual' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-semibold">Type the handle</h2>
              <p className="text-xs text-zinc-500">It looks like <code>tg_xxxxxxxxxxxx</code></p>
              <form onSubmit={submitManual} className="space-y-3">
                <Input
                  autoFocus
                  placeholder="tg_..."
                  value={manualHandle}
                  onChange={(e) => setManualHandle(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 font-mono"
                  data-testid="manual-handle-input"
                />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1 border-zinc-700 text-zinc-200" onClick={reset}>
                    Back
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" data-testid="manual-verify-btn">
                    Verify
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {view === 'checking' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-12 flex flex-col items-center gap-3 text-zinc-400">
              <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
              <p>Checking on protocol…</p>
            </CardContent>
          </Card>
        )}

        {view === 'result' && (
          <ResultCard
            result={result}
            error={error}
            requiredAge={requiredAge}
            passed={passed}
            onAgain={reset}
          />
        )}
      </main>

      <footer className="text-center py-4 text-[11px] text-zinc-600 tracking-wider border-t border-zinc-800/60">
        Powered by BHERMES · M.O.A.T.
      </footer>
    </div>
  );
}

function ResultCard({ result, error, requiredAge, passed, onAgain }) {
  if (error || !result) {
    return (
      <Card className="bg-red-950/40 border-red-900" data-testid="result-error">
        <CardContent className="p-8 text-center space-y-4">
          <ShieldAlert className="w-20 h-20 text-red-400 mx-auto" />
          <h2 className="text-2xl font-bold text-red-200">Cannot verify</h2>
          <p className="text-red-300/90 text-sm">{error || 'No badge data returned.'}</p>
          <Button onClick={onAgain} variant="outline" className="border-red-800 text-red-200" data-testid="scan-again-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Scan again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (passed) {
    return (
      <Card className="bg-emerald-600 border-0 shadow-2xl shadow-emerald-500/30" data-testid="result-pass">
        <CardContent className="p-8 text-center space-y-5">
          <ShieldCheck className="w-24 h-24 text-white mx-auto" />
          <h2 className="text-4xl font-bold text-white">PASS</h2>
          <p className="text-emerald-50 text-lg">
            Age verified <b>{result.age_bucket}+</b> — meets {requiredAge}+ requirement
          </p>
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-emerald-400/40">
            <Stat label="Tier" value={TIER_LABEL[result.trusted_tier] || 'New'} />
            <Stat label="Stays" value={result.total_stays} />
            <Stat label="Streak" value={result.current_streak} />
          </div>
          <Button onClick={onAgain} size="lg" variant="secondary" className="w-full bg-white text-emerald-700 hover:bg-emerald-50" data-testid="scan-again-btn">
            <ScanLine className="w-5 h-5 mr-2" /> Next guest
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Underage / unverified — explicit fail
  const reason = !result.age_verified
    ? 'Holder has not verified their age on the protocol.'
    : `Holder verified ${result.age_bucket}+ — does not meet the ${requiredAge}+ requirement.`;

  return (
    <Card className="bg-rose-600 border-0 shadow-2xl shadow-rose-500/30" data-testid="result-fail">
      <CardContent className="p-8 text-center space-y-5">
        <ShieldAlert className="w-24 h-24 text-white mx-auto" />
        <h2 className="text-4xl font-bold text-white">DENY</h2>
        <p className="text-rose-50 text-lg">{reason}</p>
        <Button onClick={onAgain} size="lg" variant="secondary" className="w-full bg-white text-rose-700 hover:bg-rose-50" data-testid="scan-again-btn">
          <ScanLine className="w-5 h-5 mr-2" /> Next guest
        </Button>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] tracking-widest opacity-70">{label.toUpperCase()}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}
